<template>
  <div>
    <base-header
      class="pb-9 pt-4"
      :type="$route.meta.headerClass ? $route.meta.headerClass : 'success'"
    >
    </base-header>
    <div class="container-fluid mt--9">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header d-flex align-items-center justify-content-between flex-wrap">
              <span class="h3 mb-0">Takers</span>
              <base-button type="primary" size="sm" @click="openAdd">
                <i class="fa fa-plus mr-1"></i>Add taker
              </base-button>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">
                Downstream destinations for AWS routing. Protocol and address describe the handoff;
                rights flags default bulk and activation behaviour.
              </p>
              <el-table
                class="table-card"
                :data="takers"
                row-key="id"
                header-row-class-name="thead-light"
                :default-sort="{ prop: 'name', order: 'ascending' }"
              >
                <el-table-column label="Name" prop="name" min-width="160" sortable />
                <el-table-column label="Type" min-width="130" sortable>
                  <template v-slot="{ row }">
                    {{ takerTypeLabel(row.type) }}
                  </template>
                </el-table-column>
                <el-table-column label="Protocol" prop="protocol" width="110" sortable />
                <el-table-column label="Address" min-width="220" show-overflow-tooltip>
                  <template v-slot="{ row }">
                    <span>{{ row.address }}</span>
                    <span v-if="row.port" class="text-muted ml-1 small">:{{ row.port }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Brightcove asset ID" prop="brightcoveLiveAssetId" min-width="170" show-overflow-tooltip />
                <el-table-column label="BBC audio" width="100" align="center">
                  <template v-slot="{ row }">
                    <span>{{ boolLabel(row.bbcAudioAllowed, row.defaultTakeBbcComms) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="Commercialised" width="120" align="center">
                  <template v-slot="{ row }">
                    {{ row.commercialisedAllowed !== false ? 'Yes' : 'No' }}
                  </template>
                </el-table-column>
                <el-table-column label="Enabled" width="90" align="center">
                  <template v-slot="{ row }">
                    {{ row.enabled !== false ? 'Yes' : 'No' }}
                  </template>
                </el-table-column>
                <el-table-column label="" width="160" align="right">
                  <template v-slot="{ row }">
                    <base-button size="sm" type="secondary" class="mr-1" @click="openEdit(row)">
                      Edit
                    </base-button>
                    <base-button size="sm" type="danger" @click="confirmDelete(row)">
                      Delete
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal :show.sync="showFormModal" size="lg" modal-classes="modal-scrollable">
      <div class="p-4">
        <h4 class="mb-3">{{ editingId ? 'Edit taker' : 'New taker' }}</h4>
        <div class="row">
          <div class="col-md-6 mb-3">
            <base-input label="Name" v-model="form.name" placeholder="Display name" />
          </div>
          <div class="col-md-6 mb-3">
            <base-input
              label="Key (optional)"
              v-model="form.key"
              placeholder="Stable slug"
            />
          </div>
          <div class="col-md-6 mb-3">
            <div class="form-control-label">Type</div>
            <el-select v-model="form.type" class="w-100" placeholder="Select type" @change="onTypeChange">
              <el-option label="SRT/RTMP" value="srt_rtmp" />
              <el-option label="MediaConnect" value="mediaconnect" />
              <el-option label="Brightcove" value="brightcove" />
            </el-select>
          </div>
          <div class="col-md-6 mb-3">
            <div class="form-control-label">Protocol</div>
            <el-select
              v-model="form.protocol"
              class="w-100"
              filterable
              allow-create
              default-first-option
              placeholder="Select or type"
            >
              <el-option
                v-for="option in protocolOptionsForType"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
          <div v-if="form.type === 'brightcove'" class="col-md-6 mb-3">
            <base-input
              label="Brightcove live asset ID"
              v-model="form.brightcoveLiveAssetId"
              placeholder="Required for Brightcove encoder API"
            />
          </div>
          <div class="col-12 mb-3">
            <div class="form-control-label">Address</div>
            <textarea
              v-model="form.address"
              class="form-control"
              rows="3"
              placeholder="Host, host:port, or srt://host:port (no query string)"
            />
          </div>
          <div v-if="isSrtCallerProtocol" class="col-12 mb-3">
            <base-input
              label="SRT stream ID"
              v-model="form.srtStreamId"
              placeholder="#!::r=live_…,m=publish (Castr, etc.)"
            />
            <small class="text-muted">Sent to MediaConnect as the SRT stream ID for caller outputs.</small>
          </div>
          <div v-if="isSrtProtocol" class="col-md-4 mb-3">
            <base-input
              label="Port (SRT)"
              v-model.number="form.port"
              type="number"
              placeholder="e.g. 5000"
              :min="1"
              :max="65535"
            />
          </div>
          <div v-if="isSrtCallerProtocol" class="col-md-8 mb-3">
            <base-input
              label="SRT passphrase"
              v-model="form.srtPassphrase"
              type="password"
              placeholder="Required for encrypted SRT callers"
              autocomplete="new-password"
            />
            <small class="text-muted">Stored on the taker.</small>
          </div>
          <div v-if="isSrtProtocol" class="col-md-4 mb-3">
            <base-input
              label="SRT min latency (ms)"
              v-model.number="form.srtMinLatencyMs"
              type="number"
              :placeholder="`Default (${defaultSrtLatencyMs} ms)`"
              :min="10"
              :max="15000"
            />
            <small class="text-muted">Leave blank to use global default ({{ defaultSrtLatencyMs }} ms).</small>
          </div>
          <div class="col-md-4 mb-3 d-flex align-items-center">
            <base-switch v-model="form.bbcAudioAllowed" class="mr-2" />
            <span class="small">BBC audio allowed</span>
          </div>
          <div class="col-md-4 mb-3 d-flex align-items-center">
            <base-switch v-model="form.commercialisedAllowed" class="mr-2" />
            <span class="small">Commercialised allowed</span>
          </div>
          <div class="col-md-4 mb-3 d-flex align-items-center">
            <base-switch v-model="form.enabled" class="mr-2" />
            <span class="small">Enabled</span>
          </div>
          <div class="col-12 mb-3">
            <div class="form-control-label">Notes (optional)</div>
            <textarea v-model="form.notes" class="form-control" rows="2" />
          </div>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <base-button type="secondary" class="mr-2" @click="showFormModal = false">
            Cancel
          </base-button>
          <base-button type="primary" :disabled="!form.name || saving" @click="save">
            Save
          </base-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
const DEFAULT_SRT_LATENCY_MS = 300

export default {
  name: 'TakersAdmin',
  data() {
    return {
      showFormModal: false,
      editingId: null,
      saving: false,
      defaultSrtLatencyMs: DEFAULT_SRT_LATENCY_MS,
      form: {
        name: '',
        key: '',
        type: 'srt_rtmp',
        protocol: 'srt',
        address: '',
        port: null,
        srtMinLatencyMs: null,
        srtPassphrase: '',
        srtStreamId: '',
        brightcoveLiveAssetId: '',
        bbcAudioAllowed: true,
        commercialisedAllowed: true,
        enabled: true,
        notes: '',
      },
    }
  },
  computed: {
    user() {
      return this.$store.getters['user/user']
    },
    takers() {
      return this.$store.getters['takers/array'] || []
    },
        isSrtProtocol() {
          return this.isSrtProtocolValue(this.form.protocol)
        },
        isSrtCallerProtocol() {
          return this.isSrtCallerProtocolValue(this.form.protocol)
        },
        protocolOptionsForType() {
      if (this.form.type === 'mediaconnect') {
        return [
          { label: 'MediaConnect output', value: 'mediaconnect_output' },
          { label: 'MediaLive destination', value: 'medialive_destination' },
          { label: 'SRT', value: 'srt' },
          { label: 'RTMP', value: 'rtmp' },
          { label: 'RTMPS', value: 'rtmps' },
        ]
      }
      if (this.form.type === 'brightcove') {
        return [
          { label: 'SRT', value: 'srt' },
          { label: 'RTMP', value: 'rtmp' },
          { label: 'RTMPS', value: 'rtmps' },
        ]
      }
      return [
        { label: 'SRT', value: 'srt' },
        { label: 'RTMP', value: 'rtmp' },
        { label: 'RTMPS', value: 'rtmps' },
        { label: 'Zixi', value: 'zixi' },
        { label: 'RTP', value: 'rtp' },
        { label: 'UDP', value: 'udp' },
        { label: 'RIST', value: 'rist' },
      ]
    },
  },
  mounted() {
    this.$store.dispatch('takers/fetchAndAdd')
  },
  methods: {
    isSrtProtocolValue(protocol) {
      const p = (protocol || '').toLowerCase()
      return p === 'srt' || p === 'srt-caller' || p === 'srt-listener'
    },
    isSrtCallerProtocolValue(protocol) {
      const p = (protocol || '').toLowerCase()
      return p === 'srt' || p === 'srt-caller'
    },
    formatSrtLatency(ms) {
      if (ms === null || ms === undefined || ms === '') {
        return `Default (${this.defaultSrtLatencyMs})`
      }
      return `${ms} ms`
    },
    clampSrtLatencyMs(ms) {
      return Math.min(15000, Math.max(10, ms))
    },
    boolLabel(bbcAudio, legacyDefault) {
      if (bbcAudio !== undefined) return bbcAudio !== false ? 'Yes' : 'No'
      return legacyDefault !== false ? 'Yes' : 'No'
    },
    resetForm() {
      this.form = {
        name: '',
        key: '',
        type: 'srt_rtmp',
        protocol: 'srt',
        address: '',
        port: null,
        srtMinLatencyMs: null,
        srtPassphrase: '',
        srtStreamId: '',
        brightcoveLiveAssetId: '',
        bbcAudioAllowed: true,
        commercialisedAllowed: true,
        enabled: true,
        notes: '',
      }
    },
    inferTypeFromRow(row) {
      if (row.type) return row.type
      const protocol = (row.protocol || '').toLowerCase()
      if (protocol === 'mediaconnect_output' || protocol === 'medialive_destination') {
        return 'mediaconnect'
      }
      return 'srt_rtmp'
    },
    takerTypeLabel(type) {
      if (type === 'brightcove') return 'Brightcove'
      if (type === 'mediaconnect') return 'MediaConnect'
      return 'SRT/RTMP'
    },
    onTypeChange(nextType) {
      if (nextType === 'brightcove') {
        if (!['srt', 'rtmp', 'rtmps'].includes(this.form.protocol)) this.form.protocol = 'rtmp'
        return
      }
      if (nextType === 'mediaconnect') {
        if (
          !['mediaconnect_output', 'medialive_destination', 'srt', 'rtmp', 'rtmps'].includes(
            this.form.protocol
          )
        ) {
          this.form.protocol = 'mediaconnect_output'
        }
        return
      }
      if (
        !['srt', 'rtmp', 'rtmps', 'zixi', 'rtp', 'udp', 'rist'].includes(this.form.protocol)
      ) {
        this.form.protocol = 'srt'
      }
    },
    openAdd() {
      this.editingId = null
      this.resetForm()
      this.showFormModal = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.form = {
        name: row.name || '',
        key: row.key || '',
        type: this.inferTypeFromRow(row),
        protocol: row.protocol || 'srt',
        address: row.address || '',
        port: row.port || null,
        srtMinLatencyMs:
          row.srtMinLatencyMs === null || row.srtMinLatencyMs === undefined || row.srtMinLatencyMs === ''
            ? null
            : Number(row.srtMinLatencyMs),
        srtPassphrase: row.srtPassphrase || '',
        srtStreamId: row.srtStreamId || '',
        brightcoveLiveAssetId: row.brightcoveLiveAssetId || row.liveAssetId || '',
        bbcAudioAllowed:
          row.bbcAudioAllowed !== undefined
            ? row.bbcAudioAllowed !== false
            : row.defaultTakeBbcComms !== false,
        commercialisedAllowed: row.commercialisedAllowed !== false,
        enabled: row.enabled !== false,
        notes: row.notes || '',
      }
      this.showFormModal = true
    },
    async save() {
      if (!this.form.name || this.saving) return
      if (this.form.type === 'brightcove' && !String(this.form.brightcoveLiveAssetId || '').trim()) {
        this.$notify({
          type: 'warning',
          message: 'Brightcove live asset ID is required for Brightcove takers',
          timeout: 3500,
        })
        return
      }
      this.saving = true
      const portVal = this.isSrtProtocol && this.form.port
        ? parseInt(this.form.port, 10) || null
        : null
      const latencyRaw = this.form.srtMinLatencyMs
      const srtMinLatencyMs =
        this.isSrtProtocol &&
        latencyRaw !== null &&
        latencyRaw !== undefined &&
        latencyRaw !== '' &&
        Number.isFinite(Number(latencyRaw))
          ? this.clampSrtLatencyMs(Number(latencyRaw))
          : null
      const srtPassphrase = this.isSrtCallerProtocol
        ? String(this.form.srtPassphrase || '').trim()
        : ''
      const srtStreamId = this.isSrtCallerProtocol
        ? String(this.form.srtStreamId || '').trim()
        : ''
      const payload = {
        name: this.form.name.trim(),
        key: (this.form.key && this.form.key.trim()) || '',
        type: this.form.type || 'srt_rtmp',
        protocol: (this.form.protocol && String(this.form.protocol).trim()) || 'srt',
        address: (this.form.address && this.form.address.trim()) || '',
        port: portVal,
        srtMinLatencyMs,
        srtPassphrase,
        srtStreamId,
        brightcoveLiveAssetId:
          this.form.type === 'brightcove'
            ? (this.form.brightcoveLiveAssetId && this.form.brightcoveLiveAssetId.trim()) || ''
            : '',
        bbcAudioAllowed: this.form.bbcAudioAllowed === true,
        commercialisedAllowed: this.form.commercialisedAllowed === true,
        enabled: this.form.enabled !== false,
        notes: (this.form.notes && this.form.notes.trim()) || '',
        defaultTakeBbcComms: this.form.bbcAudioAllowed === true,
      }
      try {
        if (this.editingId) {
          await this.$store.dispatch('takers/patch', { id: this.editingId, ...payload })
        } else {
          await this.$store.dispatch('takers/insert', payload)
        }
        this.showFormModal = false
        this.$notify({ type: 'success', message: 'Saved', timeout: 2000 })
      } catch (e) {
        console.error(e)
        this.$notify({
          type: 'danger',
          message: e.message || 'Save failed',
          timeout: 5000,
        })
      } finally {
        this.saving = false
      }
    },
    confirmDelete(row) {
      if (!window.confirm(`Delete taker “${row.name || row.id}”?`)) return
      this.$store
        .dispatch('takers/delete', row.id)
        .then(() => {
          this.$notify({ type: 'success', message: 'Deleted', timeout: 2000 })
        })
        .catch((e) => {
          console.error(e)
          this.$notify({
            type: 'danger',
            message: e.message || 'Delete failed',
            timeout: 5000,
          })
        })
    },
  },
}
</script>
